import { useMemo, useState } from "react";
import { useApp } from "@/store/AppStore";
import SectionTitle from "@/components/base/SectionTitle";
import { DEFAULT_KIT_ITEMS } from "@/utils/kitDefaults";

// Kit de Ventas: galería de imágenes y copias listas para compartir en redes y
// WhatsApp. El contenido lo publica y edita el administrador desde su panel.
const KitPage = () => {
  const { data } = useApp();
  const [copied, setCopied] = useState<string | null>(null);

  const items = useMemo(
    () => (data.settings.kitItems.length ? data.settings.kitItems : DEFAULT_KIT_ITEMS),
    [data.settings.kitItems]
  );

  const copy = async (id: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      /* el navegador puede bloquear el portapapeles; igual damos feedback */
    }
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  const whatsapp = (text: string) => {
    const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="pb-24">
      <SectionTitle
        text="Kit de Ventas & Marketing"
        subtitle="Imágenes y copias listas para compartir. Publicadas por tu administrador para que vendas más fácil."
      />

      {items.length === 0 ? (
        <div className="glass-panel rounded-lg px-5 py-12 text-center">
          <span className="mx-auto w-12 h-12 rounded-lg bg-background-200/50 text-accent-200 flex items-center justify-center mb-3">
            <i className="ri-image-line text-2xl" />
          </span>
          <p className="text-sm text-foreground-700 font-semibold">Todavía no hay material</p>
          <p className="text-xs text-foreground-500 mt-1">
            Tu administrador publicará imágenes y copias para compartir muy pronto.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {items.map((item) => (
            <article
              key={item.id}
              className="glass-panel rounded-lg overflow-hidden animate-fade-up flex flex-col"
            >
              <div className="w-full h-44 bg-background-200/40">
                <img
                  src={item.imageUrl}
                  alt={item.title}
                  title={`Amway · ${item.title}`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-4 flex flex-col flex-1">
                <h3 className="editable-title text-sm font-bold">{item.title}</h3>
                <p className="text-[11px] md:text-xs text-foreground-600 mt-2 flex-1">
                  {item.text}
                </p>
                <div className="flex items-center gap-2 mt-4">
                  <button
                    onClick={() => copy(item.id, item.text)}
                    className="flex-1 px-3 py-2 rounded-md border border-accent-500/35 text-accent-200 hover:bg-accent-500/10 text-[11px] font-semibold whitespace-nowrap cursor-pointer inline-flex items-center justify-center gap-1.5"
                  >
                    <i className={copied === item.id ? "ri-check-line" : "ri-file-copy-line"} />
                    {copied === item.id ? "Copiado" : "Copiar texto"}
                  </button>
                  <button
                    onClick={() => whatsapp(item.text)}
                    className="px-3 py-2 rounded-md bg-primary-500 hover:bg-primary-600 text-foreground-950 text-[11px] font-semibold whitespace-nowrap cursor-pointer inline-flex items-center justify-center gap-1.5"
                    aria-label="Compartir por WhatsApp"
                    title="Compartir por WhatsApp"
                  >
                    <i className="ri-whatsapp-line" /> Enviar
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
};

export default KitPage;